import { Metrics as MixpanelMetrics } from '@cognite/metrics';
import { useStandardMetrics as standardMetrics } from '@cognite/telemetry';

import { METRICS_NAMESPACES } from '../features/metrics/constants';

import { classifyNetworkSpeed } from './classifyNetworkSpeed';
import type { NetworkSpeed } from './classifyNetworkSpeed';
import { getConnectionDownlinkSpeedInMbps } from './get-connection-downlink-speed';
import type { QueryStatus } from './query-status';

/* Narrow, standalone shapes for the two telemetry backends — decoupled from
the concrete `@cognite/metrics`/`@cognite/telemetry` classes so tests can
inject a plain object instead of constructing a real class instance. */
export type MixpanelClient = {
  track: (name: string, properties?: Record<string, unknown>) => void;
};

export type AppDataLoadHistogram = {
  record: (
    elapsedMs: number,
    attributes: {
      'app.name': string;
      success: boolean;
      networkSpeed: NetworkSpeed;
      feature_name: string;
    }
  ) => void;
};

export type StandardMetricsResult = {
  appStandardMetrics?: {
    appDataLoadTimeHistogram?: AppDataLoadHistogram;
  };
};

export type InstrumentationDeps = {
  createMixpanelClient?: (namespace: string) => MixpanelClient;
  getStandardMetrics?: () => StandardMetricsResult;
  getConnectionDownlinkSpeedInMbps?: typeof getConnectionDownlinkSpeedInMbps;
  classifyNetworkSpeed?: typeof classifyNetworkSpeed;
};

export class Instrumentation {
  private mixpanelClient: MixpanelClient | null = null;
  private initialized = false;
  private readonly deps: Required<InstrumentationDeps>;

  constructor(deps: InstrumentationDeps = {}) {
    this.deps = {
      createMixpanelClient: deps.createMixpanelClient ?? MixpanelMetrics.create,
      getStandardMetrics: deps.getStandardMetrics ?? standardMetrics,
      getConnectionDownlinkSpeedInMbps:
        deps.getConnectionDownlinkSpeedInMbps ??
        getConnectionDownlinkSpeedInMbps,
      classifyNetworkSpeed: deps.classifyNetworkSpeed ?? classifyNetworkSpeed,
    };
  }

  init(): void {
    if (this.initialized) return;

    this.mixpanelClient = this.deps.createMixpanelClient(
      METRICS_NAMESPACES.SLI
    );

    this.initialized = true;
  }

  trackSLI(name: string, elapsedMs: number, status: `${QueryStatus}`): void {
    if (!this.initialized || elapsedMs < 0) return;

    const networkSpeedMbps = this.deps.getConnectionDownlinkSpeedInMbps();

    this.deps
      .getStandardMetrics()
      ?.appStandardMetrics?.appDataLoadTimeHistogram?.record(elapsedMs, {
        'app.name': 'infield',
        success: status === 'success',
        networkSpeed: this.deps.classifyNetworkSpeed(networkSpeedMbps),
        feature_name: `${METRICS_NAMESPACES.SLI}_${name}`,
      });
  }

  trackEvent(name: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) return;

    const networkSpeedMbps = this.deps.getConnectionDownlinkSpeedInMbps();

    this.mixpanelClient?.track(name, {
      ...properties,
      networkSpeedMbps,
    });
  }
}

export const instrumentation = new Instrumentation();
